import apiClient from './api-client.js';
import { getApiToken } from './tokenUtils.js';
import { mountReactPage, prepareReactPage } from './react-page-bootstrap.js';
import { eligiblePath, SURVEY_MOMENT_EVENT } from './survey-eligible.js';

export const SURVEY_ROUTE = 'survey';

// The page data of a moment survey (Surveys::momentAction). The event matches SurveyDefinition::KEY_PATTERN and the route requirement in routing.yml.
const MOMENT_EVENT_PATTERN = /^[a-z0-9_]{1,64}$/;
const momentDataPath = (event) => `/app/surveys-moment/${event}`;

/** The survey the server serves the query, or null for none and for a failed read. */
// surveyApi.ts eligible() reads the same response shape.
export async function readEligibleSurvey(query, deps) {
    try {
        if (!await deps.getApiToken()) return null;
        const response = await deps.client.request(eligiblePath(query), { method: 'GET' });
        if (!response.ok || response.redirected) return null;
        const body = await response.json();
        return body?.survey && typeof body.survey === 'object' ? body.survey : null;
    } catch {
        return null;
    }
}

/**
 * Reads the survey for one [data-survey-probe] island, then mounts the survey React route into it.
 * The mount root and the React chunk come only with a survey, so a page without one has neither.
 */
export async function probeSurveyIsland(island, deps) {
    const dataScript = island.querySelector('[data-react-data]');
    if (!dataScript || island.dataset.reactPage) return false;
    let data;
    try {
        data = JSON.parse(dataScript.textContent || '');
    } catch {
        return false;
    }
    const survey = await readEligibleSurvey({ surface: data.surface, event: data.event, surveyKey: data.surveyKey }, deps);
    if (!survey) return false;

    prepareReactPage(island, SURVEY_ROUTE, { ...data, survey });
    deps.mount(island);
    return true;
}

/** The page data of a moment survey, or null. The session cookie authenticates this web route. */
async function readMomentData(event, deps) {
    try {
        const response = await deps.fetch(momentDataPath(event), { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok || response.redirected) return null;
        const data = await response.json();
        return data && typeof data === 'object' ? data : null;
    } catch {
        return null;
    }
}

/**
 * Handles the moment events of one page load. The React chunk loads only when the server serves a survey.
 * One island mounts per page load. A null or failed read leaves the next event free to read again.
 */
export function createMomentProbe(deps) {
    let taken = false;
    return async function onMoment({ event, context = null } = {}) {
        if (taken || typeof event !== 'string' || !MOMENT_EVENT_PATTERN.test(event)) return false;
        taken = true;
        // The page data comes first, because a served survey opens the session window on the server.
        const data = await readMomentData(event, deps);
        const survey = data ? await readEligibleSurvey({ surface: 'moment', event, surveyKey: null }, deps) : null;
        if (!survey) {
            taken = false;
            return false;
        }
        const island = deps.document.createElement('div');
        prepareReactPage(island, SURVEY_ROUTE, { ...data, survey, triggerContext: context });
        deps.document.body.append(island);
        deps.mount(island);
        return true;
    };
}

function start() {
    const deps = {
        client: apiClient,
        getApiToken,
        mount: (island) => mountReactPage(island),
        fetch: (...args) => window.fetch(...args),
        document,
    };
    document.querySelectorAll('[data-survey-probe]').forEach((island) => {
        probeSurveyIsland(island, deps);
    });
    const onMoment = createMomentProbe(deps);
    window.addEventListener(SURVEY_MOMENT_EVENT, (event) => {
        void onMoment(event.detail ?? {});
    });
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
        start();
    }
}
